// src/lib/beds24-freshness.js — v3 ÉTAPE 1 (read-path Beds24)
//
// L'indicateur « Sync HH:MM » du bandeau (src/App.jsx) affichait historiquement
// l'heure du dernier clic de la sync iCal V1 legacy (`icalLastSync`) — trompeur :
// il ne reflète PAS la fraîcheur réelle de la synchro Beds24 (webhook + cron).
//
// Ce helper PUR calcule la vraie fraîcheur Beds24 à partir des deux signaux
// déjà exposés par le GET admin `/api/beds24-mapping` :
//   - `webhook_status.last_event_at` : dernier event webhook Beds24 reçu.
//   - `sync_state.last_poll_at`      : dernier pull cron (filet de rattrapage).
// On retient le PLUS RÉCENT des deux (la synchro est fraîche dès qu'un des deux
// canaux a transité). Alerte visuelle si en retard (défaut > 1h).

// Seuil de retard par défaut (ms) : au-delà, l'indicateur passe en alerte.
export const BEDS24_STALE_THRESHOLD_MS = 60 * 60 * 1000 // 1 h

/**
 * @param {object} args
 * @param {string|null} [args.lastEventAt] — ISO du dernier event webhook Beds24.
 * @param {string|null} [args.lastPollAt]  — ISO du dernier pull cron.
 * @param {number} [nowMs] — horloge courante (injectable pour tests).
 * @param {number} [staleThresholdMs] — seuil de retard (défaut 1 h).
 * @returns {{
 *   hasData: boolean,   // au moins un timestamp exploitable
 *   ts: number|null,    // epoch ms du plus récent des deux
 *   iso: string|null,   // ISO correspondant
 *   ageMs: number|null, // âge relatif
 *   stale: boolean,     // true si en retard (ageMs > seuil)
 * }}
 */
export function computeBeds24Freshness(
  { lastEventAt = null, lastPollAt = null, enSommeil = false } = {},
  nowMs = Date.now(),
  staleThresholdMs = BEDS24_STALE_THRESHOLD_MS,
) {
  // Lot L2 (P-3.37, 30/10) : Beds24 en sommeil (le serveur le dit, champ
  // `beds24_en_sommeil` du GET admin /api/beds24-mapping). Plus aucun signal
  // n'est attendu : l'indicateur se masque (hasData faux) au lieu de passer
  // en alerte « en retard », et ne montre pas non plus une vieille heure
  // comme si elle était fraîche.
  if (enSommeil === true) {
    return { hasData: false, ts: null, iso: null, ageMs: null, stale: false, enSommeil: true }
  }
  const candidates = []
  for (const iso of [lastEventAt, lastPollAt]) {
    if (!iso) continue
    const t = new Date(iso).getTime()
    if (!Number.isNaN(t)) candidates.push({ t, iso })
  }
  if (candidates.length === 0) {
    return { hasData: false, ts: null, iso: null, ageMs: null, stale: false }
  }
  // Plus récent des deux canaux.
  const freshest = candidates.reduce((a, b) => (b.t > a.t ? b : a))
  const ageMs = nowMs - freshest.t
  return {
    hasData: true,
    ts: freshest.t,
    iso: freshest.iso,
    ageMs,
    stale: ageMs > staleThresholdMs,
  }
}
